import { perUnitMove, signOf, type TradeType } from './primitives.ts';

interface Levels {
  tradeType: TradeType;
  entryPrice: number;
  stoplossPrice: number;
}

/**
 * The standing R, a unit: what the stop as it stands costs from this entry, null where it is not
 * behind it. What a taker risks now; `filedR` scores a recommendation, `initialRisk` a position.
 */
export function standingR(levels: Levels): number | null {
  const unit = -perUnitMove(levels.tradeType, levels.entryPrice, levels.stoplossPrice);
  return unit > 0 ? unit : null;
}

export interface Frame extends Levels {
  targetPrice: number;
  /** Null measures off the stop in the frame; a recommendation's is the analyst's, `frameOf`. */
  riskPerUnit: number | null;
}

function riskUnitOf(frame: Frame): number | null {
  if (frame.riskPerUnit !== null && frame.riskPerUnit > 0) return frame.riskPerUnit;
  return standingR(frame);
}

export function rOf(frame: Frame, price: number): number | null {
  const unit = riskUnitOf(frame);
  return unit === null ? null : perUnitMove(frame.tradeType, frame.entryPrice, price) / unit;
}

export interface Gain {
  pct: number;
  /** Null where the stop it was filed with sits past the entry. */
  r: number | null;
}

export function gainAt(frame: Frame, price: number | null): Gain | null {
  if (price === null || frame.entryPrice === 0) return null;
  return {
    pct: (perUnitMove(frame.tradeType, frame.entryPrice, price) / frame.entryPrice) * 100,
    r: rOf(frame, price),
  };
}

const NEAR_R = 0.5;
export type Proximity = 'stop' | 'target' | null;

export function proximityOf(frame: Frame, price: number | null): Proximity {
  if (price === null) return null;
  const now = rOf(frame, price);
  const stop = rOf(frame, frame.stoplossPrice);
  const target = rOf(frame, frame.targetPrice);
  if (now === null || stop === null || target === null) return null;
  if (now - stop <= NEAR_R) return 'stop';
  if (target - now <= NEAR_R) return 'target';
  return null;
}

/** The target in R from this entry; null where either side is not a gain and a loss. */
export function rewardR(
  frame: Omit<Frame, 'entryPrice' | 'riskPerUnit'> & { entryPrice: number | null },
): number | null {
  const { entryPrice } = frame;
  if (entryPrice === null) return null;
  const risk = standingR({ ...frame, entryPrice });
  const reward = perUnitMove(frame.tradeType, entryPrice, frame.targetPrice);
  return risk === null || reward <= 0 ? null : reward / risk;
}

const drawnUnit = (frame: Frame): number =>
  riskUnitOf(frame) ?? (perUnitMove(frame.tradeType, frame.entryPrice, frame.targetPrice) / 2 || 1);

// With no R, the reward is drawn as 2R wide.
export function drawableR(frame: Frame, price: number): number {
  return perUnitMove(frame.tradeType, frame.entryPrice, price) / drawnUnit(frame);
}

export function priceAtR(frame: Frame, r: number): number {
  return frame.entryPrice + signOf(frame.tradeType) * r * drawnUnit(frame);
}
